/**
 * Wishlist state: localStorage + optimistic UI + REST sync (/api/wishlist).
 * Agent WebSocket snapshots (replace/merge) update local state only — no API churn.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { getUserProfile, getUsername } from "../utils/auth";
import {
  addWishlistItem,
  removeWishlistItem,
} from "../api/wishlistApi";
import {
  trackWishlistChanged,
  isAgentActionActive,
  UI_EVENT_STALE_MS,
} from "../services/uiAgentSync";

const WishlistContext = createContext();

const getWishlistKey = (identifier) => `wishlist_${identifier}`;

const getUserIdentifier = () => {
  const profile = getUserProfile();
  if (profile?.customer_id) {
    return `customer_${profile.customer_id}`;
  }
  const username = getUsername();
  if (username) {
    return `user_${username}`;
  }
  return "guest";
};

/** Normalize wishlist items from any source (agent view_wishlist, add/remove snapshots). */
function normalizeItem(item) {
  const rawId = item.product_id ?? item.article_id ?? item.id;
  const id = rawId != null ? String(rawId) : "";
  const rawPrice = item.discounted_price ?? item.price;
  return {
    ...item,
    id,
    article_id: id,
    product_id: id,
    name: item.enhanced_prod_name ?? item.name ?? item.title ?? "",
    title: item.enhanced_prod_name ?? item.title ?? item.name ?? "",
    image: item.image_uri ?? item.image ?? item.image_url ?? "",
    price: typeof rawPrice === "number" ? rawPrice : parseFloat(String(rawPrice ?? 0)) || 0,
    currency: typeof item.currency === "string" && item.currency ? item.currency : "USD",
  };
}

/** Server sync (REST /api/wishlist) — fire-and-forget; local state stays optimistic. */
const syncWishlistToServer = async ({ article_id, action }) => {
  try {
    if (action === "add") {
      await addWishlistItem({ article_id });
    } else if (action === "remove") {
      await removeWishlistItem({ article_id });
    }
  } catch (error) {
    console.warn("[Wishlist] Server sync failed:", error);
  }
};

export function WishlistProvider({ children }) {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [currentUserIdentifier, setCurrentUserIdentifier] = useState(null);

  // --- UI → Agent sync: queue events at mutation time, flush after commit ---
  // A ref mirror of the current items lets the stable-callback mutators check
  // membership without stale closures, so no-op add/remove never sends events.
  const wishlistItemsRef = useRef([]);
  wishlistItemsRef.current = wishlistItems;
  const pendingUiEventsRef = useRef([]);
  const queueWishlistUiEvent = useCallback((event) => {
    pendingUiEventsRef.current.push({
      ...event,
      source: isAgentActionActive() ? "agent" : "manual",
      at: Date.now(),
    });
  }, []);

  useEffect(() => {
    if (!pendingUiEventsRef.current.length) return;
    const pending = pendingUiEventsRef.current;
    pendingUiEventsRef.current = [];
    const now = Date.now();
    pending.forEach(({ at, ...event }) => {
      if (now - at > UI_EVENT_STALE_MS) return; // mutation never committed
      trackWishlistChanged(event);
    });
  }, [wishlistItems]);

  const loadWishlist = useCallback(async (userIdentifier) => {
    if (!userIdentifier) {
      setWishlistItems([]);
      return;
    }

    const saved = localStorage.getItem(getWishlistKey(userIdentifier));
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setWishlistItems(Array.isArray(parsed) ? parsed.map(normalizeItem) : []);
      } catch (e) {
        console.warn("Failed to parse wishlist", e);
        setWishlistItems([]);
      }
    } else {
      setWishlistItems([]);
    }
  }, []);

  useEffect(() => {
    console.log("[WishlistContext] wishlistItems updated:", wishlistItems.length, "items");
  }, [wishlistItems]);

  // Persist for all users (guest + logged-in); agent/local remain primary UI source
  useEffect(() => {
    if (!currentUserIdentifier) return;
    try {
      localStorage.setItem(
        getWishlistKey(currentUserIdentifier),
        JSON.stringify(wishlistItems)
      );
    } catch (e) {
      console.warn("Failed to save wishlist", e);
    }
  }, [wishlistItems, currentUserIdentifier]);

  useEffect(() => {
    let intervalId = null;
    const checkAuth = () => {
      const userIdentifier = getUserIdentifier();
      if (userIdentifier !== currentUserIdentifier) {
        setCurrentUserIdentifier(userIdentifier);
        loadWishlist(userIdentifier);
      }
    };
    checkAuth();
    if (!currentUserIdentifier || currentUserIdentifier === "guest") {
      intervalId = setInterval(checkAuth, 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [currentUserIdentifier, loadWishlist]);

  const addToWishlist = useCallback((product) => {
    const rawId = product.article_id ?? product.id;
    const articleId = rawId != null ? String(rawId) : null;
    if (!articleId) return;

    const item = normalizeItem({ ...product, id: articleId, article_id: articleId });

    if (!wishlistItemsRef.current.some((i) => i.id === articleId)) {
      queueWishlistUiEvent({
        action: "add",
        productId: articleId,
        productTitle: item.title || item.name || null,
      });
    }

    setWishlistItems((prev) => {
      if (prev.some((i) => i.id === articleId)) return prev;
      // Background REST sync — skipped when no customer_id (guest)
      syncWishlistToServer({ article_id: articleId, action: "add" });
      return [...prev, item];
    });
  }, [queueWishlistUiEvent]);

  const removeFromWishlist = useCallback((id) => {
    const sid = String(id);

    const existing = wishlistItemsRef.current.find((item) => item.id === sid);
    if (existing) {
      queueWishlistUiEvent({
        action: "remove",
        productId: sid,
        productTitle: existing.title || existing.name || null,
      });
    }

    setWishlistItems((prev) => {
      const next = prev.filter((item) => item.id !== sid);
      if (next.length !== prev.length) {
        syncWishlistToServer({ article_id: sid, action: "remove" });
      }
      return next;
    });
  }, [queueWishlistUiEvent]);

  const isInWishlist = (id) => {
    const sid = String(id);
    return wishlistItems.some((item) => item.id === sid || item.article_id === sid);
  };

  /** Replace local wishlist from agent/server snapshot. No REST churn. */
  const replaceWishlistFromAgent = useCallback((items) => {
    if (!Array.isArray(items)) return;
    const normalized = items.map(normalizeItem);
    setWishlistItems(normalized);
  }, []);

  const mergeItemIntoWishlist = useCallback((product) => {
    const item = normalizeItem(product);
    if (!item.id) return;
    setWishlistItems((prev) =>
      prev.some((i) => i.id === item.id) ? prev : [...prev, item]
    );
  }, []);

  const reloadWishlist = useCallback(() => {
    if (currentUserIdentifier) {
      return loadWishlist(currentUserIdentifier);
    }
    return Promise.resolve();
  }, [currentUserIdentifier, loadWishlist]);

  return (
    <WishlistContext.Provider
      value={{
        wishlistItems,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        replaceWishlistFromAgent,
        mergeItemIntoWishlist,
        reloadWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within WishlistProvider");
  }
  return context;
}
